import { memo, useEffect, useState } from "react";

import { strings } from "@perceptron/constants";

import { ShimmerText } from "@demo/components/ToolCall/ShimmerText";
import { useMediaCarouselStore } from "@demo/store/media-carousel";
import { AgentState } from "@demo/store/model";
import { mediaKindOfExtension } from "@demo/utils/media";

/** What the model is doing with the prompt while it waits for its first token, by the media it holds. */
function useWorkingStatus(): string {
  const media = useMediaCarouselStore((state) => state.mediaInAgentCarousel);
  const kinds = media.map((item) => mediaKindOfExtension(item.extension));
  const status = strings.agentOutput.status;
  if (kinds.includes("video")) return status.video;
  if (kinds.includes("audio")) return status.audio;
  const images = kinds.filter((kind) => kind === "image").length;
  if (images > 1) return status.images;
  if (images === 1) return status.image;
  return status.text;
}

const LoadingDots = memo(function LoadingDots({ agentState }: { agentState: AgentState }) {
  const workingStatus = useWorkingStatus();
  const [dots, setDots] = useState("");

  useEffect(() => {
    if (agentState !== AgentState.IDLE) {
      const dotsInterval = setInterval(() => {
        setDots((prev) => {
          if (prev.length >= 3) return "";
          return prev + ".";
        });
      }, 500);

      return () => clearInterval(dotsInterval);
    } else {
      setDots("");
    }
  }, [agentState]);

  if (agentState === AgentState.LOADING) {
    return `${strings.agentOutput.loading}${dots}`;
  } else if (agentState === AgentState.EVALUATING || agentState === AgentState.STREAMING) {
    // Until the first token, a shimmering line says what the model is doing rather than a generic wait.
    return (
      <ShimmerText className="tool-call-text-soft" data-testid="working-status" isStreaming>
        {workingStatus}
      </ShimmerText>
    );
  } else {
    return strings.agentOutput.awaitingPrompt;
  }
});

export default LoadingDots;
